// ===========================================
// Merge-field insertion — the two rules a copy must not lose
// ===========================================
//
// EXTRACTED from `CustomEmailEditorPage.insertMergeField` (2026-09-17) so the
// email-sequence editor inherits the behaviour rather than a plausible-looking
// reimplementation of it. The full handler stays page-side — it closes over
// that page's refs and setters — but the two rules that are easy to get wrong,
// and silently, live here.
//
// The older `TemplateEditorDialog` implementation has neither rule: it is
// onClick-only (no caret preservation, no double-insert risk because no
// mousedown) and it REPLACES the selection. Do not model on it.

/**
 * Should this event be ignored as the duplicate half of a mouse activation?
 *
 * ⚠️ A real mouse click fires `mousedown` AND `click`. With a handler on both
 * (which is what keeps the chips caret-accurate AND keyboard-operable), a
 * single click would insert the token TWICE — `{{parentName}}{{parentName}}`.
 *
 * A keyboard activation arrives as a `click` with `detail === 0` and is the one
 * that must be let through: swallowing every click would make the chips
 * keyboard-dead, which is the accessibility half of why both handlers exist.
 */
export function isDuplicateMouseActivation(e: { type: string; detail: number }): boolean {
  return e.type === 'click' && e.detail !== 0;
}

/**
 * Where a token should land in a plain input, and the resulting value.
 *
 * ⚠️ INSERT AFTER A SELECTION, NEVER REPLACE IT. Splicing
 * `slice(0, start) + token + slice(end)` DELETES whatever the admin had
 * selected, with no undo — the value is React-controlled, so Ctrl+Z cannot
 * bring it back. Selecting a word in order to "personalise it" is a plausible
 * gesture, and silently eating their text is a poor answer to it. Inserting at
 * the selection's end is non-destructive and still lands where they pointed.
 */
export function insertAtSelectionEnd(
  input: { value: string; selectionStart: number | null; selectionEnd: number | null },
  placeholder: string
): { value: string; caret: number } {
  const at = input.selectionEnd ?? input.selectionStart ?? input.value.length;
  return {
    value: input.value.slice(0, at) + placeholder + input.value.slice(at),
    // The caret goes AFTER what was just inserted, so a second chip does not
    // land inside the first.
    caret: at + placeholder.length,
  };
}
